/**
 * お金の画面の小さな判断（2026-09-27 money/dashboard-3ds-self）: 前払いの入金の結果・自分の支払い（売上に含めない）の見せ方。
 *
 * WHY moneyLogic.ts に足さないか: moneyLogic.ts は 400 行を超えている（300 行の目安）。今回の 2 つは pages/money の画面だけが使うので、
 * 画面の隣に置く。moneyLogic.ts と同じく import を持たない = `node --test`（型の除去だけ）で動く（moneyDisplay.test.ts）。
 * 文言はここに書かない（返すのは状態の名前。画面が i18n のキーに引き直す）。
 */

// ---------------------------------------------------------------- 前払いの入金の結果

/** サーバーの failure_reason（apps/server/src/modules/payments/protection.ts CARD_NOT_PROTECTED・migration 0251） */
export const CARD_NOT_PROTECTED = 'card_not_protected';

/**
 * 入金（GET /v1/money/prepaid/checkouts/:id・POST …/checkout の応答）の状態を、画面が言うこと 1 つにまとめる。
 *   done          … 残高に入った（status = completed のときだけ）
 *   checking      … まだ決まっていない（pending）。「確認しています」
 *   card_refused  … このカードでは入金できない（3D セキュアで守られないカードを断った）→ 別のカードを促す
 *   failed        … それ以外の失敗・期限切れ（請求はされていない）
 *
 * WHY completed のときだけ done か: 3ds-refuse（2026-09-27）から入金は手動キャプチャ = Checkout から戻った時点では
 * 「オーソリ済み・判定前」で、そのあと断られる（オーソリ取り消し）ことがある。戻った・ポップアップが閉じた = 完了、と扱うと
 * 一瞬「入金しました」と出てから断られる。状態が completed になるまでは checking のまま。
 *
 * WHY 断った理由（3D セキュア・不正対策）を画面に書かないか: 断る条件を不正をする側に教えない（founder 判断 2026-09-27）。
 * 本物の利用者に必要なのは「このカードはだめ・別のカードで」だけ。
 */
export type TopUpOutcome = 'done' | 'checking' | 'card_refused' | 'failed';

export function topUpOutcome(t: { status: string; failureReason?: string | null }): TopUpOutcome {
  if (t.status === 'completed') return 'done';
  if (t.status === 'pending') return 'checking';
  if (t.status === 'failed' && t.failureReason === CARD_NOT_PROTECTED) return 'card_refused';
  // expired・failed（authorization_canceled 等）・未知の状態: 入金されていない側に倒す（「入金しました」と誤って言わない）
  return 'failed';
}

/**
 * Checkout の「戻る（キャンセル）」から戻ったか。戻り先の URL にこの印を付けておく（PrepaidPage の TopUpCard）。
 * WHY: サーバーは成功・キャンセルのどちらの戻り先にも `?kumo_topup=<id>` を付ける（prepaidTopup.ts withTopUpParam）。
 * キャンセルでは入金は pending のまま（期限切れまで）なので、印が無いと 20 秒「確認しています」を出し続けてしまう。
 */
export const TOPUP_CANCELED_PARAM = 'kumo_topup_canceled';

// ---------------------------------------------------------------- 自分の支払い（売上に含めない）

/**
 * GET /v1/money/self-purchases の状態（apps/server/src/modules/money/selfPurchase.ts・0252）。
 *   excluded … 保留中（売上に含めず、出金できない勘定にある。期日に前払いへ移す）
 *   prepaid  … 前払いへ移した（利用料に使える・出金はできない）
 *   released … 運営が解除した（誤判定 = 通常の売上へ戻した）
 */
export type SelfPurchaseStatus = 'excluded' | 'prepaid' | 'released';
const SELF_STATUSES: readonly string[] = ['excluded', 'prepaid', 'released'];

/** 画面の i18n キーの末尾。未知の状態（サーバーが足した）は null = 画面はコードをそのまま出す（壊れない） */
export function selfPurchaseStatusKey(status: string): SelfPurchaseStatus | null {
  return SELF_STATUSES.includes(status) ? (status as SelfPurchaseStatus) : null;
}

/**
 * 一覧の 1 行で見せる額: いまの状態で「その購入がどれだけ動いたか」。
 *   excluded → いま出金できない勘定にある額（heldNow。一部返金の後は減る）
 *   prepaid  → 前払いへ移した額
 *   released → 売上へ戻した額（古い行で null なら総額）
 * WHY 総額（gross）だけにしないか: 総額は払った額で、決済手数料・返金の後に動いた額と違う。状態の列と並べて読める額にする
 */
export function selfPurchaseAmount(r: { status: string; gross: number; heldNow: number; movedToPrepaid: number; releasedAmount: number | null }): number {
  if (r.status === 'excluded') return r.heldNow;
  if (r.status === 'prepaid') return r.movedToPrepaid;
  if (r.status === 'released') return r.releasedAmount ?? r.gross;
  return r.gross;
}

/**
 * 売上の「収入源別」の表に載せる行。自分の支払い（source = self_purchase）は除く。
 * WHY: 売上に含めない額を売上の表に混ぜると「売上として数えられた」ように読める。自分の支払いは別のカードで説明つきで見せる
 */
export function earningSources<T extends { source: string }>(rows: T[]): T[] {
  return rows.filter((r) => r.source !== 'self_purchase');
}

/**
 * 自分の支払いのカードを出すか。0 のとき（出金できない勘定が 0・一覧も空）は出さない（関係の無い人に説明を見せない）。
 * 一覧だけある（すべて前払いへ移した・解除した）ときも出す = 「売上が消えた」と思わせないため、どこへ行ったかを見せる
 */
export function showSelfPurchases(balance: number | undefined, listed: number, byGame: Array<{ selfPurchases?: number }> = []): boolean {
  return (balance ?? 0) > 0 || listed > 0 || byGame.some((g) => (g.selfPurchases ?? 0) > 0);
}
